/**
 * Topbar — the sticky header of the authenticated shell.
 *
 * Layout is: the slide-over trigger (below `lg` only), a page-context slot, then
 * the account controls. `children` is that slot; when a page passes nothing the bar
 * falls back to the label of the matching nav item, so the current location is
 * still named on the screens where the sidebar is hidden. The label comes from the
 * same `navSections` constant the sidebar renders, so the two can never disagree.
 */
import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { Menu } from 'lucide-react';
import { Button } from '../ui/Button';
import { NotificationBell } from './NotificationBell';
import { ThemeToggle } from './ThemeToggle';
import { UserMenu } from './UserMenu';
import { findNavItem } from './Sidebar';
import { cn } from '../../lib/cn';

export interface TopbarProps extends HTMLAttributes<HTMLElement> {
  onOpenSidebar: () => void;
  /** Drives `aria-expanded` on the menu button. */
  isSidebarOpen?: boolean;
  /** Page context: a title, a search field, a filter — anything the page owns. */
  children?: ReactNode;
}

export const Topbar = forwardRef<HTMLElement, TopbarProps>(function Topbar(
  { onOpenSidebar, isSidebarOpen = false, children, className, ...props },
  ref,
) {
  const { pathname } = useLocation();
  const fallbackLabel = findNavItem(pathname)?.label;

  return (
    <header
      ref={ref}
      className={cn(
        'sticky top-0 z-30 border-b border-border bg-surface/80 backdrop-blur',
        className,
      )}
      {...props}
    >
      <div className="flex h-16 items-center gap-3 px-4 sm:px-6 lg:px-8">
        <Button
          variant="ghost"
          size="icon"
          onClick={onOpenSidebar}
          aria-label="Open navigation"
          aria-controls="app-sidebar"
          aria-expanded={isSidebarOpen}
          className="-ml-1 shrink-0 lg:hidden"
        >
          <Menu className="h-5 w-5" aria-hidden="true" />
        </Button>

        <div className="min-w-0 flex-1">
          {children ??
            (fallbackLabel ? (
              <p className="truncate text-sm font-semibold text-content">{fallbackLabel}</p>
            ) : null)}
        </div>

        <div className="flex shrink-0 items-center gap-1">
          <ThemeToggle />
          <NotificationBell />
          <span className="mx-1 hidden h-6 w-px bg-border sm:block" aria-hidden="true" />
          <UserMenu />
        </div>
      </div>
    </header>
  );
});
